import { useEffect, useMemo, useState } from "react";
import {
  ShieldCheck,
  FileText,
  Activity,
  Send,
  Copy as CopyIcon,
  Download,
  RefreshCcw,
  MessageSquare,
  BarChart3,
  Users,
  Search,
  ExternalLink,
  Save,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import { api, rupiah } from "../lib/api";
import { toast } from "sonner";
import { AdminSideExport } from "./Admin";

/* Shell that wraps admin content with sidebar */
function AdminShell({ active, children }) {
  return (
    <div className="dashboard">
      <AdminSideExport active={active} />
      <main className="dash-main">{children}</main>
    </div>
  );
}

/* ============================================================
   ADMIN — LEGAL & COMPLIANCE
   ============================================================ */
export function AdminLegal() {
  const [form, setForm] = useState({
    business_name: "MASTOFA.COM",
    business_type: "",
    nib: "",
    kbli: "",
    npwp: "",
    pse_status: "",
    pse_number: "",
    address: "",
    responsible_person: "",
    nib_doc_url: "",
    npwp_doc_url: "",
    pse_doc_url: "",
    other_doc_url: "",
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState("");

  useEffect(() => {
    api("/admin/settings/legal").then((d) => setForm({ ...form, ...d })).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      await api("/admin/settings/legal", { method: "PATCH", data: form });
      toast.success("Legal & Compliance disimpan.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal simpan.");
    } finally {
      setBusy(false);
    }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api("/admin/media/upload", {
        method: "POST",
        data: fd,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm({ ...form, [`${kind}_doc_url`]: r.url });
      toast.success(`Dokumen ${kind.toUpperCase()} berhasil diupload.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload gagal.");
    } finally {
      setUploading("");
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-head">
        <div>
          <span className="section-kicker">LEGAL & COMPLIANCE</span>
          <h1>Identitas Bisnis & Dokumen Legal</h1>
          <p className="muted">
            Data ini digunakan pada halaman legal, invoice, dan tampilan publik non-sensitif.
            Nomor NIB, NPWP, dan PSE tidak ditampilkan ke pengunjung — hanya nama bisnis, alamat,
            dan status PSE.
          </p>
        </div>
        <button className="button" disabled={busy} onClick={save} data-testid="legal-save">
          <Save size={16} /> {busy ? "Menyimpan…" : "Simpan"}
        </button>
      </div>

      <div className="admin-card">
        <h3>Identitas Bisnis</h3>
        <div className="two-col">
          <label>
            Nama Bisnis
            <input value={form.business_name} onChange={set("business_name")}
              data-testid="legal-business-name" />
          </label>
          <label>
            Bentuk Bisnis
            <select value={form.business_type} onChange={set("business_type")}
              data-testid="legal-business-type">
              <option value="">Pilih…</option>
              <option>Perorangan / UMKM</option>
              <option>CV</option>
              <option>PT</option>
              <option>Yayasan</option>
              <option>Koperasi</option>
            </select>
          </label>
          <label style={{ gridColumn: "1 / -1" }}>
            Alamat Lengkap
            <textarea rows="2" value={form.address} onChange={set("address")}
              data-testid="legal-address" />
          </label>
          <label>
            Penanggung Jawab
            <input value={form.responsible_person} onChange={set("responsible_person")}
              data-testid="legal-responsible" />
          </label>
          <label>
            Status PSE
            <select value={form.pse_status} onChange={set("pse_status")}
              data-testid="legal-pse-status">
              <option value="">Pilih…</option>
              <option>Terdaftar</option>
              <option>Belum Terdaftar</option>
              <option>Tidak Wajib</option>
              <option>Dalam Proses</option>
            </select>
          </label>
        </div>
      </div>

      <div className="admin-card">
        <h3>Nomor Legalitas <span className="pill-warn">Sensitif — non-publik</span></h3>
        <div className="two-col">
          <label>NIB<input value={form.nib} onChange={set("nib")} data-testid="legal-nib" /></label>
          <label>KBLI<input value={form.kbli} onChange={set("kbli")} data-testid="legal-kbli" placeholder="misal: 85499" /></label>
          <label>NPWP<input value={form.npwp} onChange={set("npwp")} data-testid="legal-npwp" /></label>
          <label>Nomor Pendaftaran PSE<input value={form.pse_number} onChange={set("pse_number")} data-testid="legal-pse-number" /></label>
        </div>
      </div>

      <div className="admin-card">
        <h3>Dokumen (Upload)</h3>
        <div className="two-col">
          {["nib", "npwp", "pse", "other"].map((k) => (
            <div key={k} className="upload-row" data-testid={`legal-upload-${k}`}>
              <label>
                {k === "other" ? "Dokumen Lain" : `Dokumen ${k.toUpperCase()}`}
                <input type="file" accept="application/pdf,image/*"
                  onChange={(e) => upload(k, e.target.files?.[0])}
                  disabled={uploading === k} />
              </label>
              {form[`${k}_doc_url`] && (
                <a href={form[`${k}_doc_url`]} target="_blank" rel="noreferrer"
                   className="text-link" style={{ fontSize: 12 }}>
                  Lihat <ExternalLink size={12} />
                </a>
              )}
              {uploading === k && <small className="muted">Mengunggah…</small>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   ADMIN — INVOICES
   ============================================================ */
export function AdminInvoices() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [emailStatus, setEmailStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (status) params.set("status", status);
      if (emailStatus) params.set("email_status", emailStatus);
      const r = await api(`/admin/invoices?${params.toString()}`);
      setRows(r || []);
    } catch (err) {
      toast.error("Gagal memuat invoice.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, emailStatus]);

  const download = async (inv) => {
    try {
      const token = localStorage.getItem("mastofa_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/invoices/${inv.id}/pdf`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await r.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${inv.number}.pdf`;
      link.click();
    } catch {
      toast.error("Gagal download PDF.");
    }
  };

  const resend = async (inv) => {
    try {
      const r = await api(`/admin/invoices/${inv.id}/resend`, { method: "POST" });
      toast.success(`Email invoice: ${r.email_status}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal resend.");
    }
  };

  const backfill = async () => {
    try {
      const r = await api("/admin/invoices/backfill", { method: "POST" });
      toast.success(`Backfill: ${r.created} invoice baru dibuat.`);
      load();
    } catch {
      toast.error("Backfill gagal.");
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-head">
        <div>
          <span className="section-kicker">INVOICE</span>
          <h1>Semua Invoice</h1>
          <p className="muted">
            Invoice dibuat otomatis ketika order berstatus PAID. Nomor unik atomic per hari:
            <code style={{ marginLeft: 6 }}>INV-YYYYMMDD-XXXXX</code>.
          </p>
        </div>
        <button className="button button-ghost" onClick={backfill} data-testid="invoice-backfill">
          <RefreshCcw size={15} /> Backfill invoice pesanan lama
        </button>
      </div>

      <div className="admin-card admin-toolbar">
        <div className="toolbar-row">
          <div className="search-input">
            <Search size={15} />
            <input placeholder="Cari nomor / email / order id"
              value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              data-testid="invoice-search" />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            data-testid="invoice-status-filter">
            <option value="">Semua status</option>
            <option value="PAID">PAID</option>
            <option value="VOID">VOID</option>
          </select>
          <select value={emailStatus} onChange={(e) => setEmailStatus(e.target.value)}
            data-testid="invoice-email-filter">
            <option value="">Email: semua</option>
            <option value="SENT">SENT</option>
            <option value="PENDING">PENDING</option>
            <option value="FAILED">FAILED</option>
            <option value="SKIPPED_NO_KEY">SKIPPED (no key)</option>
          </select>
          <button className="button" onClick={load} data-testid="invoice-refresh">
            {loading ? "Memuat…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="admin-table">
        <table>
          <thead>
            <tr>
              <th>Nomor</th>
              <th>Tanggal</th>
              <th>Customer</th>
              <th>Layanan</th>
              <th style={{ textAlign: "right" }}>Total</th>
              <th>Email</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan="7" className="empty">Belum ada invoice.</td></tr>
            )}
            {rows.map((inv) => (
              <tr key={inv.id} data-testid={`invoice-row-${inv.number}`}>
                <td><b>{inv.number}</b></td>
                <td>{inv.invoice_date}</td>
                <td>
                  {inv.customer_name}<br />
                  <small className="muted">{inv.customer_email}</small>
                </td>
                <td>{inv.items?.[0]?.title || "-"}</td>
                <td style={{ textAlign: "right" }}>{rupiah(inv.total)}</td>
                <td>
                  <span className={`pill pill-${(inv.email_status || "PENDING").toLowerCase()}`}>
                    {inv.email_status || "PENDING"}
                  </span>
                </td>
                <td className="table-actions">
                  <button onClick={() => download(inv)} data-testid={`invoice-download-${inv.number}`}>
                    <Download size={13} /> PDF
                  </button>
                  <button onClick={() => resend(inv)} data-testid={`invoice-resend-${inv.number}`}>
                    <Send size={13} /> Resend
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================================================
   ADMIN — TRACKING STUDIO
   ============================================================ */
export function AdminTracking() {
  const [form, setForm] = useState({
    meta_pixel_id: "",
    meta_capi_token: "",
    meta_capi_test_event_code: "",
    tiktok_pixel_id: "",
    tiktok_events_token: "",
    tiktok_events_test_code: "",
    ga4_measurement_id: "",
    ga4_api_secret: "",
    google_ads_conversion_id: "",
    google_ads_conversion_label: "",
    gtm_container_id: "",
    custom_head: "",
    custom_body: "",
    custom_body_end: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api("/admin/settings/tracking").then((d) => setForm({ ...form, ...d })).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      await api("/admin/settings/tracking", { method: "PATCH", data: form });
      toast.success("Tracking config disimpan. Reload halaman untuk melihat script aktif.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal simpan.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-head">
        <div>
          <span className="section-kicker">TRACKING STUDIO</span>
          <h1>Pixel, CAPI & Analytics</h1>
          <p className="muted">
            Semua ID dan token disimpan di server. Token CAPI / Events API / API Secret tidak pernah
            di-expose ke browser — hanya dipakai server-side saat forwarding event lewat
            <code>&nbsp;/api/track/event</code>.
          </p>
        </div>
        <button className="button" disabled={busy} onClick={save} data-testid="tracking-save">
          <Save size={16} /> {busy ? "Menyimpan…" : "Simpan"}
        </button>
      </div>

      <div className="admin-card">
        <h3>Meta (Facebook / Instagram) Pixel + Conversions API</h3>
        <div className="two-col">
          <label>Pixel ID<input value={form.meta_pixel_id} onChange={set("meta_pixel_id")}
            placeholder="123456789012345" data-testid="tracking-meta-pixel" /></label>
          <label>CAPI Access Token <span className="pill-warn">Server</span>
            <input type="password" value={form.meta_capi_token} onChange={set("meta_capi_token")}
              placeholder="EAA…" data-testid="tracking-meta-token" /></label>
          <label>Test Event Code (opsional)
            <input value={form.meta_capi_test_event_code} onChange={set("meta_capi_test_event_code")}
              placeholder="TEST12345" data-testid="tracking-meta-test" /></label>
        </div>
      </div>

      <div className="admin-card">
        <h3>TikTok Pixel + Events API</h3>
        <div className="two-col">
          <label>Pixel ID<input value={form.tiktok_pixel_id} onChange={set("tiktok_pixel_id")}
            placeholder="CXX…" data-testid="tracking-tiktok-pixel" /></label>
          <label>Events API Token <span className="pill-warn">Server</span>
            <input type="password" value={form.tiktok_events_token} onChange={set("tiktok_events_token")}
              data-testid="tracking-tiktok-token" /></label>
          <label>Test Event Code (opsional)
            <input value={form.tiktok_events_test_code} onChange={set("tiktok_events_test_code")}
              data-testid="tracking-tiktok-test" /></label>
        </div>
      </div>

      <div className="admin-card">
        <h3>Google Analytics 4 & Google Ads</h3>
        <div className="two-col">
          <label>GA4 Measurement ID
            <input value={form.ga4_measurement_id} onChange={set("ga4_measurement_id")}
              placeholder="G-XXXXXXXXXX" data-testid="tracking-ga4" /></label>
          <label>GA4 API Secret (Measurement Protocol)
            <input type="password" value={form.ga4_api_secret} onChange={set("ga4_api_secret")}
              data-testid="tracking-ga4-secret" /></label>
          <label>Google Ads Conversion ID
            <input value={form.google_ads_conversion_id} onChange={set("google_ads_conversion_id")}
              placeholder="AW-XXXXXXX" data-testid="tracking-ads-id" /></label>
          <label>Google Ads Conversion Label
            <input value={form.google_ads_conversion_label} onChange={set("google_ads_conversion_label")}
              data-testid="tracking-ads-label" /></label>
        </div>
      </div>

      <div className="admin-card">
        <h3>Google Tag Manager</h3>
        <div className="two-col">
          <label>GTM Container ID
            <input value={form.gtm_container_id} onChange={set("gtm_container_id")}
              placeholder="GTM-XXXXXXX" data-testid="tracking-gtm" /></label>
        </div>
      </div>

      <div className="admin-card">
        <h3>Custom Script (Advanced)</h3>
        <p className="muted" style={{ marginBottom: 12 }}>
          Kode HTML yang di-inject ke halaman. Hanya admin yang bisa mengedit. Jangan masukkan
          kredensial server-side apapun (OpenAI, Duitku, Telegram) di sini.
        </p>
        <label>&lt;head&gt; (paling awal)
          <textarea rows="4" value={form.custom_head} onChange={set("custom_head")}
            data-testid="tracking-custom-head" /></label>
        <label>&lt;body&gt; setelah opening tag
          <textarea rows="4" value={form.custom_body} onChange={set("custom_body")}
            data-testid="tracking-custom-body" /></label>
        <label>&lt;body&gt; sebelum closing
          <textarea rows="4" value={form.custom_body_end} onChange={set("custom_body_end")}
            data-testid="tracking-custom-body-end" /></label>
      </div>
    </div>
  );
}

/* ============================================================
   ADMIN — AI ANALYTICS
   ============================================================ */
export function AdminAiAnalytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api(`/admin/ai/analytics?days=${days}`);
      setData(r);
    } catch {
      toast.error("Gagal memuat analytics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const maxDay = useMemo(
    () => Math.max(1, ...(data?.per_day || []).map((d) => d.count)),
    [data]
  );

  return (
    <div className="admin-page">
      <div className="admin-head">
        <div>
          <span className="section-kicker">AI ANALYTICS</span>
          <h1>Yang customer tanyakan ke AI</h1>
          <p className="muted">
            Ringkasan percakapan dengan Mas Tofa AI. Gunakan data ini untuk memahami masalah
            paling sering dialami customer.
          </p>
        </div>
        <select value={days} onChange={(e) => setDays(parseInt(e.target.value))}
          data-testid="ai-analytics-range" className="button-select">
          <option value="7">7 hari</option>
          <option value="14">14 hari</option>
          <option value="30">30 hari</option>
          <option value="90">90 hari</option>
        </select>
      </div>

      {loading && <div className="muted">Memuat…</div>}
      {data && (
        <>
          <div className="admin-metrics">
            <div className="metric-card">
              <span className="metric-label">Total pertanyaan</span>
              <b>{data.total_questions.toLocaleString("id-ID")}</b>
            </div>
            <div className="metric-card">
              <span className="metric-label">User unik</span>
              <b>{data.unique_users.toLocaleString("id-ID")}</b>
            </div>
            <div className="metric-card">
              <span className="metric-label">Topic teratas</span>
              <b>{data.top_topics?.[0]?.topic || "—"}</b>
              <small className="muted">{data.top_topics?.[0]?.count || 0} pertanyaan</small>
            </div>
            <div className="metric-card">
              <span className="metric-label">Rata-rata / hari</span>
              <b>{Math.round(data.total_questions / days)}</b>
            </div>
          </div>

          <div className="admin-card">
            <h3>Pertanyaan per hari</h3>
            <div className="ai-chart" data-testid="ai-chart">
              {data.per_day.map((d) => (
                <div key={d.date} className="ai-chart-bar" title={`${d.date}: ${d.count}`}>
                  <span style={{ height: `${(d.count / maxDay) * 100}%` }} />
                  <small>{d.date.slice(5)}</small>
                </div>
              ))}
              {data.per_day.length === 0 && <span className="muted">Belum ada data.</span>}
            </div>
          </div>

          <div className="admin-two">
            <div className="admin-card">
              <h3>Topik terpopuler</h3>
              <ul className="ai-list">
                {data.top_topics.map((t) => (
                  <li key={t.topic} data-testid={`ai-topic-${t.topic}`}>
                    <b>{t.topic}</b>
                    <span className="pill">{t.count}</span>
                  </li>
                ))}
                {data.top_topics.length === 0 && <li className="muted">Belum ada.</li>}
              </ul>
            </div>
            <div className="admin-card">
              <h3>Pertanyaan paling sering</h3>
              <ul className="ai-list">
                {data.top_questions.slice(0, 10).map((q, i) => (
                  <li key={i} data-testid={`ai-question-${i}`}>
                    <span style={{ flex: 1 }}>{q.prompt.slice(0, 100)}</span>
                    <span className="pill">{q.count}</span>
                  </li>
                ))}
                {data.top_questions.length === 0 && <li className="muted">Belum ada.</li>}
              </ul>
            </div>
          </div>

          <div className="admin-card">
            <h3>Percakapan terbaru (30)</h3>
            <div className="admin-table" style={{ maxHeight: 400, overflow: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>Waktu</th>
                    <th>Email</th>
                    <th>Konteks</th>
                    <th>Topic</th>
                    <th>Pertanyaan</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent.map((r, i) => (
                    <tr key={i}>
                      <td><small>{r.created_at?.slice(0, 16)?.replace("T", " ")}</small></td>
                      <td><small>{r.user_email || "-"}</small></td>
                      <td><small className="pill">{r.context}</small></td>
                      <td><small>{r.topic}</small></td>
                      <td><small>{r.prompt?.slice(0, 120)}</small></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ============================================================
   ADMIN — LEADS
   ============================================================ */
export function AdminLeads() {
  const [rows, setRows] = useState([]);
  const [service, setService] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => {
    const p = new URLSearchParams();
    if (service) p.set("service", service);
    if (status) p.set("status", status);
    try {
      const r = await api(`/admin/leads?${p.toString()}`);
      setRows(r || []);
    } catch {
      toast.error("Gagal memuat leads.");
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, status]);

  const updateStatus = async (id, s) => {
    try {
      await api(`/admin/leads/${id}`, { method: "PATCH", data: { status: s, note } });
      toast.success("Status di-update.");
      setNote("");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal update.");
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-head">
        <div>
          <span className="section-kicker">LEAD MANAGEMENT</span>
          <h1>Konsultasi & Inquiry</h1>
          <p className="muted">
            Data konsultasi dari halaman Jasa Iklan, Landing Page, dan konsultasi lain.
          </p>
        </div>
      </div>

      <div className="admin-card admin-toolbar">
        <div className="toolbar-row">
          <select value={service} onChange={(e) => setService(e.target.value)}
            data-testid="lead-service-filter">
            <option value="">Semua layanan</option>
            <option value="jasa_iklan">Jasa Iklan</option>
            <option value="landing_page">Landing Page</option>
            <option value="privat">Privat</option>
            <option value="vip">VIP</option>
            <option value="course">Course</option>
            <option value="konsultasi">Konsultasi</option>
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            data-testid="lead-status-filter">
            <option value="">Semua status</option>
            <option value="NEW">NEW</option>
            <option value="CONTACTED">CONTACTED</option>
            <option value="QUALIFIED">QUALIFIED</option>
            <option value="FOLLOW_UP">FOLLOW_UP</option>
            <option value="CONVERTED">CONVERTED</option>
            <option value="LOST">LOST</option>
          </select>
          <button className="button button-ghost" onClick={load} data-testid="lead-refresh">
            <RefreshCcw size={14} /> Refresh
          </button>
        </div>
      </div>

      <div className="admin-table">
        <table>
          <thead>
            <tr>
              <th>Tanggal</th>
              <th>Layanan</th>
              <th>Nama</th>
              <th>WhatsApp</th>
              <th>Bisnis / Produk</th>
              <th>Target</th>
              <th>Sumber</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan="9" className="empty">Belum ada lead.</td></tr>
            )}
            {rows.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`}>
                <td><small>{(l.created_at || "").slice(0, 10)}</small></td>
                <td><span className="pill">{l.service}</span></td>
                <td><b>{l.name}</b><br /><small className="muted">{l.email}</small></td>
                <td>
                  <a href={`https://wa.me/${l.whatsapp}`} target="_blank" rel="noreferrer" className="text-link">
                    {l.whatsapp}
                  </a>
                </td>
                <td>
                  {l.business || "-"}
                  {l.product && <><br /><small>{l.product}</small></>}
                </td>
                <td>{l.revenue_target || "-"}</td>
                <td><small>{l.utm_source || l.referrer?.slice(0, 20) || "direct"}</small></td>
                <td><span className={`pill pill-${(l.status || "new").toLowerCase()}`}>{l.status}</span></td>
                <td>
                  <button onClick={() => setSelected(l)} data-testid={`lead-open-${l.id}`}>
                    Detail
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="modal-backdrop" onClick={() => setSelected(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{selected.name}</h3>
            <p className="muted">{selected.service} · {selected.whatsapp} · {selected.email || "no email"}</p>
            <div className="lead-detail-grid">
              {[
                ["Bisnis", selected.business],
                ["Produk", selected.product],
                ["Marketplace", selected.marketplace],
                ["Harga", selected.price_range],
                ["Omzet skrng", selected.monthly_revenue],
                ["Target omzet", selected.revenue_target],
                ["Platform iklan", selected.ad_platform],
                ["Budget iklan", selected.ad_budget],
                ["UTM source", selected.utm_source],
                ["UTM campaign", selected.utm_campaign],
                ["Page", selected.page],
              ].map(([k, v]) => (
                <div key={k}>
                  <small className="muted">{k}</small>
                  <b>{v || "-"}</b>
                </div>
              ))}
            </div>
            {selected.main_problem && (
              <>
                <b>Masalah utama</b>
                <p>{selected.main_problem}</p>
              </>
            )}
            {selected.notes?.length > 0 && (
              <>
                <b>Notes</b>
                <ul className="lead-notes">
                  {selected.notes.map((n, i) => (
                    <li key={i}><small className="muted">{n.at?.slice(0, 16)} · {n.by}</small><br />{n.text}</li>
                  ))}
                </ul>
              </>
            )}
            <label>
              Tambah note (opsional saat ganti status)
              <textarea rows="2" value={note} onChange={(e) => setNote(e.target.value)}
                data-testid="lead-note" />
            </label>
            <div className="lead-actions-row">
              {["CONTACTED", "QUALIFIED", "FOLLOW_UP", "CONVERTED", "LOST"].map((s) => (
                <button key={s} className="button-ghost" onClick={() => updateStatus(selected.id, s)}
                  data-testid={`lead-status-${s}`}>
                  {s}
                </button>
              ))}
              <button className="button-ghost" onClick={() => setSelected(null)}>Tutup</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   ADMIN — COMPLAINTS
   ============================================================ */
export function AdminComplaints() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const load = async () => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    try {
      const r = await api(`/admin/complaints?${p.toString()}`);
      setRows(r || []);
    } catch {
      toast.error("Gagal memuat pengaduan.");
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const change = async (id, s) => {
    try {
      await api(`/admin/complaints/${id}`, { method: "PATCH", data: { status: s } });
      toast.success("Status di-update.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal update.");
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-head">
        <div>
          <span className="section-kicker">PENGADUAN</span>
          <h1>Tiket Pengaduan Customer</h1>
        </div>
      </div>

      <div className="admin-card admin-toolbar">
        <div className="toolbar-row">
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            data-testid="complaint-status-filter">
            <option value="">Semua status</option>
            <option value="OPEN">OPEN</option>
            <option value="IN_PROGRESS">IN_PROGRESS</option>
            <option value="RESOLVED">RESOLVED</option>
            <option value="REJECTED">REJECTED</option>
            <option value="CLOSED">CLOSED</option>
          </select>
        </div>
      </div>

      <div className="admin-table">
        <table>
          <thead>
            <tr>
              <th>Ticket</th>
              <th>Tanggal</th>
              <th>Nama</th>
              <th>Kontak</th>
              <th>Subjek</th>
              <th>Order ID</th>
              <th>Status</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="8" className="empty">Belum ada pengaduan.</td></tr>}
            {rows.map((c) => (
              <tr key={c.id} data-testid={`complaint-row-${c.ticket}`}>
                <td><b>{c.ticket}</b></td>
                <td><small>{(c.created_at || "").slice(0, 10)}</small></td>
                <td>{c.name}</td>
                <td><small>{c.contact}</small></td>
                <td>{c.subject}</td>
                <td><small>{c.order_id || "-"}</small></td>
                <td><span className={`pill pill-${(c.status || "open").toLowerCase()}`}>{c.status}</span></td>
                <td>
                  <select value={c.status} onChange={(e) => change(c.id, e.target.value)}
                    data-testid={`complaint-status-select-${c.ticket}`}>
                    <option value="OPEN">OPEN</option>
                    <option value="IN_PROGRESS">IN_PROGRESS</option>
                    <option value="RESOLVED">RESOLVED</option>
                    <option value="REJECTED">REJECTED</option>
                    <option value="CLOSED">CLOSED</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================================================
   Shell-wrapped exports (used by App.js routes)
   ============================================================ */
const _Legal = AdminLegal;
const _Invoices = AdminInvoices;
const _Tracking = AdminTracking;
const _AiAnalytics = AdminAiAnalytics;
const _Leads = AdminLeads;
const _Complaints = AdminComplaints;

export function AdminLegalPage()      { return <AdminShell active="legal"><_Legal /></AdminShell>; }
export function AdminInvoicesPage()   { return <AdminShell active="invoices"><_Invoices /></AdminShell>; }
export function AdminTrackingPage()   { return <AdminShell active="tracking"><_Tracking /></AdminShell>; }
export function AdminAiAnalyticsPage(){ return <AdminShell active="ai-analytics"><_AiAnalytics /></AdminShell>; }
export function AdminLeadsPage()      { return <AdminShell active="leads"><_Leads /></AdminShell>; }
export function AdminComplaintsPage() { return <AdminShell active="complaints"><_Complaints /></AdminShell>; }
